/*
 * Quicken Simple Amount Field (QAmountField)
 */

import numeral from 'numeral';
// BASE
import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

import { usePreferenceV2 } from 'app-components/flux/preferences-v2';

// @ts-expect-error TS7016
import getSymbolFromCurrency from 'currency-symbol-map';

// MUI COMPONENTS
import InputAdornment from '@mui/material/InputAdornment';
import type { InputBaseProps } from '@mui/material/InputBase';
import InputBase from '@mui/material/InputBase';
import type { TextFieldProps, TextFieldVariants } from '@mui/material/TextField';
import TextField from '@mui/material/TextField';
import Typography, { type TypographyProps, type TypographyVariant } from '@mui/material/Typography';
import { isBrowser } from 'utils/utils';

type valType = number | string;

type overridenTypes = 'variant' | 'margin' | 'onSubmit' | 'onBlur' | 'onChange' | 'onFocus' | 'editable';

type NonEditable = { editable?: false; variant?: TypographyVariant };

export type QAmountProps = {
  InputProps?: Partial<InputBaseProps>;
  onSubmit?: (e?: any) => void;
  onFocus?: (e?: any) => void;
  onBlur?: (e?: any) => void;
  onChange?: (e?: any) => void; // returns an event with target.value as the updated value.
  // Would love to change it to just return the value, but we need it this way as long as we want to keep using formik (it wants an event)
  value: valType;
  currency?: string;
  submitOnBlur?: boolean;
  hideCurrency?: boolean;
  disableUnderline?: boolean;
  autoFocus?: boolean;
  id?: string;
  inputId?: string; // ID for the <input> component
  textFieldProps?: TextFieldProps;
  styleProps?: any;
  removeSpacing?: boolean; // removes space between sign and amount
  size?: string;
  wrapperStyles?: any; // styling for the wrapper div so that input can fill a parent container
  noNegatives?: boolean;
  textFieldVariant?: TextFieldVariants;
  margin?: 'dense' | 'none' | 'normal';
  className?: string;
  decimals?: number; // default to 2, replace omitCents with 0
  fullWidth?: boolean;
  submitOnTab?: boolean; // leave logic the same but submit with tab in register
  maxChars?: number;
  maxVal?: number; //  Only works with positive values
  minVal?: number; //  Only works with negative values
  name?: string;
  styles?: any;
  classes?: any;

  placeholder?: string; // optional placeholder
  expectedSign?: 'positive' | 'negative';
  showSign?: 'positive' | 'negative' | 'both';
  showColor?: 'positive' | 'negative' | 'both';
  disabled?: boolean;
  plain?: boolean; // if plain, then it uses a basic input and doesn't use a textField for styling

  slotProps?: TextFieldProps['slotProps'];
  typographyProps?: TypographyProps; // for !editable mode, pass in typography props
} & ({ editable: true; variant?: TextFieldVariants } | NonEditable) &
  Partial<Omit<TypographyProps, overridenTypes>> &
  Partial<Omit<InputBaseProps, overridenTypes>> &
  Partial<Omit<TextFieldProps, overridenTypes>>;

export const expectedSignEnum = Object.freeze({
  // used for default sign display. Sign will show if it differs from expected sign
  positive: 'positive',
  negative: 'negative',
});
export const showSignEnum = Object.freeze({
  // force display of this sign when present
  positive: 'positive',
  negative: 'negative',
  both: 'both',
});
export const showColorEnum = Object.freeze({
  // apply green color to positive and red to negative amounts
  positive: 'positive',
  negative: 'negative',
  both: 'both',
});

// STYLES HOOK CREATION
const useStyles = makeStyles()(({ palette }) => ({
  // empty classes here for override purposes (protect against complaints)
  inputField: {}, // ^
  input: {}, // ^
  adornment: {
    alignSelf: 'center',
    marginRight: 3,
  },
  noWrap: {
    whiteSpace: 'nowrap',
  },
  positive: {
    color: palette.amount.positive,
  },
  negative: {
    color: palette.amount.negative,
  },
  fullWidth: {
    width: '100%',
  },
  disabledTitle: {
    color: palette.text.secondary,
  },
}));

const preserveCaretPosition = (e: any, offset = 0) => {
  const caretPosition = (e?.target?.selectionStart || 0) + offset;
  e.persist();
  setTimeout(() => e.target?.setSelectionRange?.(caretPosition, caretPosition), 0);
};

const QAmountField = React.forwardRef((props: QAmountProps, ref: any) => {
  // PROPS ============================================================================
  const {
    removeSpacing,
    editable,
    InputProps,
    value,
    currency,
    hideCurrency,
    onSubmit,
    onBlur,
    disableUnderline,
    submitOnBlur,
    autoFocus,
    id,
    textFieldProps,
    styleProps,
    onChange,
    size,
    wrapperStyles,
    noNegatives,
    fullWidth,
    placeholder,
    textFieldVariant,
    margin,
    className,
    showSign,
    decimals = 2,
    expectedSign = expectedSignEnum.negative,
    showColor,
    plain = false,
    maxChars,
    maxVal,
    minVal,
    inputId,
    onFocus,
    typographyProps,
    styles,
    disabled,
    slotProps = {},
    submitOnTab,
    ...otherProps
  } = props;
  const {
    htmlInput: slotPropsHtmlInput = {},
    input: slotPropsInput = {},
    inputLabel: slotPropsInputLabel,
    ...slotPropsOther
  } = slotProps;

  const expectedSignInternal = noNegatives ? expectedSignEnum.positive : expectedSign;

  // FORMATTING FUNCTIONS

  const applySign = (val?: number | string, formatString?: string) => {
    if (placeholder && val === '') {
      return { sign: '', numberString: '' };
    }

    const newVal = Number(val);
    const negativeSign = noNegatives ? '' : '-';

    const sign = Math.sign(newVal);
    let signChar = sign >= 0 ? '' : negativeSign;
    // by default, show sign if different than expected
    if (expectedSignInternal === expectedSignEnum.positive) {
      signChar = newVal < 0 ? '-' : '';
    } else if (expectedSignInternal === expectedSignEnum.negative) {
      signChar = newVal > 0 ? '+' : '';
    }
    // if showSign provided, show based on that
    if (showSign && newVal !== 0) {
      signChar = '';
      if (newVal > 0) {
        signChar = showSign === showSignEnum.positive || showSign === showSignEnum.both ? '+' : '';
      } else if (newVal < 0) {
        signChar = showSign === showSignEnum.negative || showSign === showSignEnum.both ? '-' : '';
      }
    }
    if (newVal === 0 || Number.isNaN(newVal)) {
      // no sign
      signChar = '';
    }

    const numberString =
      placeholder && Number.isNaN(newVal) ? '' : numeral(Math.abs(newVal).toFixed(decimals)).format(formatString); // toFixed address numeral return NaN for small numbers like 1.1001100110011002e-9
    return { sign: signChar, numberString };
  };
  // SELECTORS ==========================================================================

  const currencySymbol = hideCurrency ? '' : getSymbolFromCurrency(currency || 'USD');

  // 2 decimals are required, but trim 2+ trailing decimals e.g. 0.10 0.01 0.123 0.1234
  const decimalsFormat = useMemo(
    () =>
      !decimals ? '' : `.${decimals <= 2 ? '0'.repeat(decimals) : `${'0'.repeat(2)}[${'0'.repeat(decimals - 2)}]`}`,
    [decimals],
  );

  const renderStaticValue = (val: valType) => {
    const formatString = `0,00${decimalsFormat}`;
    const parts = applySign(val, formatString);
    return removeSpacing
      ? `${parts.sign}${currencySymbol}${parts.numberString}`
      : `${parts.sign}${currencySymbol} ${parts.numberString}`;
  };
  const renderEditableValue = (val: valType, withComma = false) => {
    const commaString = withComma ? '0,00' : '00';
    const formatString = decimals === 0 ? commaString : `${commaString}${decimalsFormat}`;
    const parts = applySign(val, formatString);
    return `${parts.sign}${parts.numberString}`;
  };

  // STATE ============================================================================
  const [hasFocus, setHasFocus] = useState(false);
  const [fieldValue, setFieldValue] = useState(() =>
    editable ? renderEditableValue(value, !hasFocus) : renderStaticValue(value),
  );

  // EFFECTS ============================================================================
  useEffect(() => {
    if (!hasFocus) {
      const newValue = editable ? renderEditableValue(value, !hasFocus) : renderStaticValue(value);

      if (
        (typeof maxVal !== 'number' || Number(newValue) <= maxVal) &&
        (typeof minVal !== 'number' || Number(newValue) >= minVal)
      ) {
        setFieldValue(newValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, hasFocus, expectedSignInternal]);

  // STYLES ============================================================================
  const { classes, cx } = useStyles();

  const validateField = (val: valType) => {
    if (placeholder && val === '') {
      return null;
    }
    const currentString = String(val).replace(/[^0-9.+/-]/g, '');
    let newVal = Number.isNaN(Number(currentString)) ? 0 : Number(currentString);
    if (!(currentString.includes('-') || currentString.includes('+'))) {
      // if no strict signage, apply sign
      // if showSign value is set (and not 'both'), then sign omission means it is the opposite of the showSign value
      if (showSign && showSign === showSignEnum.positive) {
        newVal *= -1;
      } else if (showSign && showSign === showSignEnum.negative) {
        // keep positive
      } else {
        newVal *= expectedSignInternal === expectedSignEnum.negative ? -1 : 1;
      }
    }
    return newVal.toFixed(decimals);
  };

  // INTERNAL FUNCTIONS
  const handleKeyDown = (e: any) => {
    const valueNumber = Number(e?.target?.value?.replaceAll?.(',', '') || e?.target?.value);
    const legalChars = '0123456789,';

    if (noNegatives && e.key === '-') {
      // Only allow negative when noNegatives is false
      e.preventDefault();
      return;
    }

    let firstVal;
    let caretOffset;
    let killUpdate;
    let newValue = fieldValue;
    let selection = '';

    switch (e.key) {
      // allow normal stuff
      case 'Backspace':
        if (onChange) preserveCaretPosition(e, -1);
        break;
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'ArrowUp':
      case 'ArrowDown':
        break;
      case 'Enter':
        onSubmit?.({ target: { value: validateField(fieldValue) } });
        break;
      case 'Tab':
        if (!submitOnBlur || submitOnTab) {
          onSubmit?.({ target: { value: validateField(fieldValue) } });
        }
        break;
      case '.': // only allow one period
        // don't allow if no decimals
        if (decimals === 0) e.preventDefault();
        // if period already exists, make sure it's in the selection to replace it
        if (fieldValue.indexOf('.') !== -1) {
          // has a period
          selection = fieldValue.substring(e.target.selectionStart, e.target.selectionEnd);
          if (selection.indexOf('.') !== -1) {
            // in current selection
            preserveCaretPosition(e, 1);
          } else {
            e.preventDefault();
          }
        }
        break;
      case ',':
        // only allow commas if the cursor isn't already next to a comma
        selection = fieldValue.substring(e.target.selectionStart, e.target.selectionEnd);
        if (selection && selection.indexOf(',') !== -1) {
          // replacing a comma, let it roll
          break;
        } else if (fieldValue.substring(e.target.selectionStart - 1, e.target.selectionEnd + 1).indexOf(',') !== -1) {
          // next to a comma
          e.preventDefault();
        }
        break;
      case '-':
      case '+':
        caretOffset = 0;
        killUpdate = false;
        firstVal = fieldValue?.[0];
        if (firstVal) {
          selection = fieldValue.substring(e.target.selectionStart, e.target.selectionEnd);
          if (
            selection &&
            selection?.length > 0 && // valid selection and
            (selection.indexOf('-') !== -1 ||
              selection.indexOf('+') !== -1 || // contains a sign to override
              selection?.length === e.target.value?.length) // or is the entirety of the field
          ) {
            // Happy, let the replace do it's thing
          } else {
            e.preventDefault();
            if (firstVal === '-' || firstVal === '+') {
              // sign already exists
              if (e.key === firstVal) {
                // same sign - do nothing
                killUpdate = true;
              } else if (
                (typeof maxVal !== 'number' || valueNumber <= maxVal) &&
                (typeof minVal !== 'number' || valueNumber >= minVal)
              ) {
                // replace with new sign
                newValue = `${e.key}${fieldValue?.slice(1, fieldValue?.length)}`;
                setFieldValue(newValue);
              }
            } else if (
              (typeof maxVal !== 'number' || valueNumber <= maxVal) &&
              (typeof minVal !== 'number' || valueNumber >= minVal)
            ) {
              // sign not in the string, so add it
              newValue = `${e.key}${fieldValue}`;
              setFieldValue(newValue);
              caretOffset = 1;
            }
            // reset caret position after update
            if (!killUpdate) {
              preserveCaretPosition(e, caretOffset);
              if (
                onChange &&
                (typeof maxVal !== 'number' || valueNumber <= maxVal) &&
                (typeof minVal !== 'number' || valueNumber >= minVal)
              ) {
                onChange?.({ target: { value: validateField(newValue) } });
              }
            }
          }
        }
        break;
      case 'a':
      case 'c':
      case 'v':
        if (!(e.metaKey || e.ctrlKey)) {
          // allow copy and paste and select
          e.preventDefault();
        }
        break;
      default:
        if (legalChars.indexOf(e.key) === -1) {
          // prevent illegal characters
          e.preventDefault();
        } else if (e.target.selectionStart === 0 && e.target.selectionEnd === 0) {
          // if at the front
          firstVal = fieldValue?.[0];
          if (
            firstVal &&
            (firstVal === '-' || firstVal === '+') &&
            (typeof maxVal !== 'number' || valueNumber <= maxVal) &&
            (typeof minVal !== 'number' || valueNumber >= minVal)
          ) {
            // and there is a sign
            e.preventDefault();
            // move new entry behind sign;
            newValue = `${firstVal}${e.key}${fieldValue.slice(1, fieldValue?.length)}`;
            setFieldValue(newValue);
            preserveCaretPosition(e, 2);
            if (onChange) {
              onChange?.({ target: { value: validateField(newValue) } });
            }
          }
        }
        break;
    }
  };
  const handleFocus = (e: any) => {
    if (e && e.currentTarget) {
      if (isBrowser('safari') && e?.target) {
        setTimeout(() => {
          // This is required to override Safari default behavior
          e.target.setSelectionRange(0, e.target.value.length);
        }, 20);
      } else {
        e.currentTarget.select();
      }
    }
    if (onFocus) {
      onFocus();
    }
    setHasFocus(true);
  };
  const handleChange = (e: any) => {
    if (e && e.target) {
      if (placeholder && e.target.value === '') {
        setFieldValue(e.target.value);
        onChange?.({ target: { value: e.target.value } });
        return;
      }

      const valueNumber = Number(e?.target?.value?.replaceAll?.(',', '') || e?.target?.value);

      if ((e.target.value.split('.') || []).length - 1 > 1) {
        // if multiple periods
        // don't save
        preserveCaretPosition(e, -1);
      } else if (
        (typeof maxVal !== 'number' || valueNumber <= maxVal) &&
        (typeof minVal !== 'number' || valueNumber >= minVal)
      ) {
        // standard save
        setFieldValue(e.target.value);
        // if controlled, update form with validated/formatted value
        onChange?.({ target: { value: validateField(e.target.value) } });
      }
    }
  };
  const handleBlur = (e: any) => {
    const func = submitOnBlur ? onSubmit : onBlur;
    if (func) {
      e.target.value = validateField(fieldValue);
      func(e);
    }
    setHasFocus(false);
  };
  const handlePaste = (e: any) => {
    if (e?.clipboardData) {
      e.preventDefault();
      const textData = (e.originalEvent || e)?.clipboardData?.getData?.('Text');
      if (textData) {
        // sanitize data to paste
        window.document.execCommand('insertText', false, textData.trim()?.replace(/[^0-9,.+/-]/g, ''));
      }
    }
  };

  // RENDER ============================================================================

  let colorClassName;
  const currentVal = Number(validateField(fieldValue));

  if (currentVal !== 0 && showColor) {
    if (currentVal > 0 && (showColor === showColorEnum.positive || showColor === showColorEnum.both)) {
      colorClassName = classes.positive;
    } else if (currentVal < 0 && (showColor === showColorEnum.negative || showColor === showColorEnum.both)) {
      colorClassName = classes.negative;
    }
  }

  const [sPrivacyMode] = usePreferenceV2('sPrivacyMode');

  return (
    <div
      id={id}
      sharedcomponentid='QAmountField'
      data-testid='QAmountField'
      className={fullWidth ? cx(classes.fullWidth, className) : className}
      style={{ ...wrapperStyles }}
    >
      {plain &&
        (editable ? (
          <InputBase
            {...otherProps}
            autoFocus={autoFocus}
            classes={{
              ...otherProps?.classes,
              root: colorClassName,
            }}
            id={inputId}
            ref={ref}
            value={fieldValue}
            placeholder={placeholder}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onPaste={handlePaste}
            disabled={props.disabled}
            type={sPrivacyMode ? 'password' : 'text'}
          />
        ) : (
          <Typography
            id={inputId}
            variant='paragraphRegular'
            className={cx(classes.input, props.classes?.input, colorClassName)}
            noWrap
            {...typographyProps}
            {...(otherProps as Extract<QAmountProps, NonEditable>)}
            ref={ref}
          >
            {sPrivacyMode ? '$***' : String(fieldValue)}
          </Typography>
        ))}

      {!plain &&
        (!editable ? (
          <>
            {otherProps?.label && (
              <Typography className={classes.disabledTitle} variant='captionRegular'>
                {otherProps?.label}
              </Typography>
            )}
            <Typography
              id={inputId}
              variant='bodyRegular'
              className={cx(classes.noWrap, colorClassName)}
              {...(otherProps as Extract<QAmountProps, NonEditable>)}
              {...typographyProps}
              style={{ ...styles, ...styleProps }}
            >
              {sPrivacyMode ? '$***' : String(fieldValue)}
            </Typography>
          </>
        ) : (
          <TextField
            {...otherProps}
            {...textFieldProps}
            id={inputId}
            ref={ref}
            onChange={handleChange}
            value={fieldValue}
            placeholder={placeholder}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onPaste={handlePaste}
            autoFocus={autoFocus}
            fullWidth={fullWidth}
            size={size}
            variant={textFieldVariant}
            margin={margin}
            disabled={props.disabled}
            type={sPrivacyMode ? 'password' : 'text'}
            slotProps={{
              htmlInput: {
                className: classes.inputField,
                maxLength: maxChars || 20,
                name: otherProps?.name,
                ...slotPropsHtmlInput,
              },
              // @ts-expect-error TS2322
              input: {
                ...InputProps,
                classes: {
                  root: colorClassName,
                  ...InputProps?.classes,
                  ...otherProps?.classes,
                },
                ...(disableUnderline && { disableUnderline: true }),
                className: classes.inputField,
                startAdornment: (
                  <InputAdornment
                    classes={{ root: classes.adornment }}
                    disableTypography
                    position='start'
                    sx={{ color: props.disabled ? 'text.disabled' : 'text.primary' }}
                  >
                    {currencySymbol}
                  </InputAdornment>
                ),
                ...slotPropsInput,
              },
              inputLabel: {
                sx: { whiteSpace: 'nowrap' },
                ...slotPropsInputLabel,
              },
              ...slotPropsOther,
            }}
          />
        ))}
    </div>
  );
});

QAmountField.displayName = 'QAmountField';

export default QAmountField;
